import { memo } from 'react'

import { edgeMidpoints } from '@/geo/polygon'
import { pctToView, type Ring } from '@/geo/types'

/**
 * Vertex and edge-midpoint handles for the selected shape (§3.2).
 *
 * Vertices are squares, edge midpoints are smaller hollow circles — a
 * different silhouette, not a different colour, so the difference survives
 * on a busy plate and for a colour-blind operator. Radii arrive already
 * divided by the zoom so a handle is always the same size on screen (§9.2:
 * 5 px handle, 10 px hit, 14 px on touch).
 */

export interface VertexHandlesProps {
  id: string
  ring: Ring
  /** Handle radius in percent-of-width (screen px ÷ k ÷ pxPerPct). */
  radius: number
  selectedIndex?: number
  hoverIndex?: number
  hoverEdge?: number
  /** Hide the "insert a vertex here" handles while a drag is in flight. */
  showMidpoints?: boolean
  colour: string
}

export const VertexHandles = memo(function VertexHandles({
  id,
  ring,
  radius,
  selectedIndex,
  hoverIndex,
  hoverEdge,
  showMidpoints = true,
  colour,
}: VertexHandlesProps) {
  const r = Math.max(0.25, radius)
  const mids = showMidpoints && ring.length >= 3 ? edgeMidpoints(ring) : []

  return (
    <g className="vertex-handles">
      {showMidpoints
        ? mids.map((m, i) => {
            const [x, y] = pctToView(m)
            return (
              <circle
                key={`e${i}`}
                data-id={id}
                data-edge={i}
                cx={x}
                cy={y}
                r={r * 0.62}
                fill="white"
                fillOpacity={hoverEdge === i ? 0.95 : 0.55}
                stroke={colour}
                strokeWidth={r * 0.28}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: 'copy' }}
              />
            )
          })
        : null}
      {ring.map((p, i) => {
        const [x, y] = pctToView(p)
        const active = selectedIndex === i || hoverIndex === i
        const size = r * (active ? 2.3 : 1.9)
        return (
          <rect
            key={`v${i}`}
            data-id={id}
            data-vertex={i}
            x={x - size / 2}
            y={y - size / 2}
            width={size}
            height={size}
            rx={size * 0.18}
            fill={active ? colour : 'white'}
            stroke={active ? 'white' : colour}
            strokeWidth={r * 0.3}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: 'grab' }}
          >
            <title>{`Point ${i + 1} of ${ring.length} — drag to move, Alt+click to remove`}</title>
          </rect>
        )
      })}
    </g>
  )
})
